import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { convertFullsizeToHalfSize } from '@common/helpers';
import { SpecialAssistanceRequestState } from '@common/helpers/constants/my-booking/passenger-information-input-modal.state';
import { SpecialAssistanceRequestFoldingTypeData } from '@common/interfaces/mybooking/modals/passenger-information-input/special-assistance-request-modal.interface';
import { AswValidators } from '@lib/helpers/validate/validators';
import { ValidationErrorInfo } from '@lib/interfaces/validate';

/**
 * サポート情報入力モーダル
 */
@Component({
  selector: 'asw-special-assistance-request-modal-folding-type',
  templateUrl: './special-assistance-request-modal-folding-type.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialAssistanceRequestModalFoldingTypeComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input()
  get data() {
    return this._data;
  }
  set data(value: SpecialAssistanceRequestFoldingTypeData | undefined) {
    this._data = value;
    this.enableChair();
  }
  private _data?: SpecialAssistanceRequestFoldingTypeData;

  @Output()
  dataChange: EventEmitter<SpecialAssistanceRequestFoldingTypeData> =
    new EventEmitter<SpecialAssistanceRequestFoldingTypeData>();

  public id = this.constructor.name;
  public isFoldable?: boolean;
  public selectError: ValidationErrorInfo | null = null;

  public formGroup = new FormGroup({
    depth: new FormControl<number | null>(null, [
      AswValidators.required(SpecialAssistanceRequestState.Depth),
      AswValidators.numeric(SpecialAssistanceRequestState.Depth),
    ]),
    width: new FormControl<number | null>(null, [
      AswValidators.required(SpecialAssistanceRequestState.Width),
      AswValidators.numeric(SpecialAssistanceRequestState.Width),
    ]),
    height: new FormControl<number | null>(null, [
      AswValidators.required(SpecialAssistanceRequestState.Height),
      AswValidators.numeric(SpecialAssistanceRequestState.Height),
    ]),
    weight: new FormControl<number | null>(null, [
      AswValidators.required(SpecialAssistanceRequestState.Weight),
      AswValidators.numeric(SpecialAssistanceRequestState.Weight),
    ]),
  });

  @ViewChild('depthInputElement') depthInput?: ElementRef;
  @ViewChild('widthInputElement') widthInput?: ElementRef;
  @ViewChild('heightInputElement') heightInput?: ElementRef;
  @ViewChild('weightInputElement') weightInput?: ElementRef;

  constructor(private _change: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.refresh();
  }

  ngAfterViewChecked(): void {
    this.changeInputSize(this.depthInput);
    this.changeInputSize(this.widthInput);
    this.changeInputSize(this.heightInput);
    this.changeInputSize(this.weightInput);
  }

  ngOnDestroy(): void {
    this.apply();
  }

  refresh() {
    this.isFoldable = this.data?.isFolding;

    if (this.data) {
      this.formGroup.controls.depth.setValue(this.data.depth !== 0 ? this.data.depth : null);
      this.formGroup.controls.height.setValue(this.data.height !== 0 ? this.data.height : null);
      this.formGroup.controls.width.setValue(this.data.width !== 0 ? this.data.width : null);
      this.formGroup.controls.weight.setValue(this.data.weight !== 0 ? this.data.weight : null);
    }

    this.enableChair();
  }

  /**
   * 車いすサイズ入力可否処理
   */
  enableChair(): void {
    if (this.isFoldable === true || this.isFoldable === undefined) {
      this.formGroup.disable();
      return;
    }

    this.formGroup.enable();
  }

  // (39) 折りたたみ可否の値更新処理　※サポート情報入力モーダル(サイズ情報・折りたたみグループ)
  onChangeFoldable(value: boolean = false) {
    this.isFoldable = value;
    this.selectError = null;

    this.enableChair();
  }

  onChangeDepth(element?: ElementRef) {
    if (element?.nativeElement.value) {
      this.formGroup.controls.depth.setValue(
        parseInt(convertFullsizeToHalfSize(element?.nativeElement.value, 'reserveInfo'))
      );
    }
    this.changeInputSize(element);
  }

  onChangeWidth(element?: ElementRef) {
    if (element?.nativeElement.value) {
      this.formGroup.controls.width.setValue(
        parseInt(convertFullsizeToHalfSize(element?.nativeElement.value, 'reserveInfo'))
      );
    }
    this.changeInputSize(element);
  }

  onChangeHeight(element?: ElementRef) {
    if (element?.nativeElement.value) {
      this.formGroup.controls.height.setValue(
        parseInt(convertFullsizeToHalfSize(element?.nativeElement.value, 'reserveInfo'))
      );
    }
    this.changeInputSize(element);
  }

  onChangeWeight(element?: ElementRef) {
    if (element?.nativeElement.value) {
      this.formGroup.controls.weight.setValue(
        parseInt(convertFullsizeToHalfSize(element?.nativeElement.value, 'reserveInfo'))
      );
    }
    this.changeInputSize(element);
  }

  changeInputSize(element?: ElementRef): string {
    if (!element) {
      return '10px';
    }
    this.selectError = null;
    const length: number = element.nativeElement.value.length;
    element.nativeElement.style.width = '10px';

    if (length === 2) {
      element.nativeElement.style.width = '20px';
    }

    if (length === 3) {
      element.nativeElement.style.width = '30px';
    }

    return element.nativeElement.style.width;
  }

  /**
   * エラー判定
   * @returns 判定結果
   */
  public isError() {
    this.selectError = SpecialAssistanceRequestState.FoldingType;
    if (this.isFoldable || (!this.isFoldable && this.formGroup.valid)) {
      this.selectError = null;
      return false;
    }
    this.formGroup.markAllAsTouched();
    this._change.detectChanges();
    return true;
  }

  apply(): void {
    if (!this.data) {
      return;
    }

    if (this.isError()) {
      return;
    }

    this.data.isFolding = this.isFoldable;
    this.data.depth = this.isFoldable ? 0 : this.formGroup.controls.depth.value ?? 0;
    this.data.width = this.isFoldable ? 0 : this.formGroup.controls.width.value ?? 0;
    this.data.height = this.isFoldable ? 0 : this.formGroup.controls.height.value ?? 0;
    this.data.weight = this.isFoldable ? 0 : this.formGroup.controls.weight.value ?? 0;

    this.dataChange.emit(this.data);
  }

  /**
   * エラー取得
   * @param data フォームコントロール
   * @returns エラー内容
   */
  getError(data: FormControl): ValidationErrorInfo | undefined {
    return data.touched && data.errors ? (Object.values(data.errors)[0] as ValidationErrorInfo) : undefined;
  }
}
